import { enqueueSnackbar } from '~/utils/notifications';
import { useCallback, useState } from 'react';
import { ContourTexture } from '~/engine/data/structure/ContourTexture';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { SDFOperations } from '~/engine/util/algorithms/sdf/SDFOperations';
import { Environment } from '~/engine/data/Environment';
import { getErrorMessage } from '~/utils/errors';
import { waitForUI } from '~/utils/uiUtils';
import { useGetOrCreateDestination } from '../shared/useGetOrCreateDestination';
import { makeApplyGeometryDeps } from '../shared/applyGeometryDeps';
import { applyComputedGeometry } from '~/hooks/structure-set/applyComputedGeometry';
import { recordContourEdit } from '~/hooks/structure-set/recordContourEdit';

interface UseRingApplicationOptions {
    environment: Environment | null;
    inputRoi: RegionOfInterest | undefined;
    onSuccess: () => void;
    updateView: () => void;
    /**
     * Optional: hand off a cached preview texture instead of recomputing.
     * Returns undefined if no cached result is available for the given input.
     */
    consumeRingPreview?: (
        lowerMm: number,
        upperMm: number
    ) => ContourTexture | undefined;
}

export function useRingApplication({
    environment,
    inputRoi,
    onSuccess,
    updateView,
    consumeRingPreview,
}: UseRingApplicationOptions) {
    const [isProcessing, setIsProcessing] = useState(false);
    const { getOrCreateDestination } = useGetOrCreateDestination({
        environment,
    });

    const handleApplyRing = useCallback(
        async (
            lowerMm: number,
            upperMm: number,
            outputRoiNumber: number | null,
            newName?: string
        ) => {
            if (isProcessing) {
                enqueueSnackbar(
                    'Another ring operation is already in progress.',
                    {
                        variant: 'warning',
                    }
                );
                return;
            }

            if (!environment?.structureSet || !inputRoi?.texture) {
                enqueueSnackbar(
                    'Select a structure with contours to apply this operation.',
                    { variant: 'warning' }
                );
                return;
            }

            setIsProcessing(true);

            // The compute is identical in both branches; only the destination
            // (new vs overwrite) differs.
            const inputTexture = inputRoi.texture;
            const computeRingTexture = async (): Promise<ContourTexture> => {
                const cached = consumeRingPreview?.(lowerMm, upperMm);
                if (cached) {
                    return cached;
                }
                enqueueSnackbar('Applying ring...', {
                    variant: 'info',
                });
                // TODO: improve kind of hacky rendering solutions should be removed and
                // heavy computation should be lifted away from blocking the main thread for smooth rendering.
                await waitForUI();
                return SDFOperations.applyRing(inputTexture, lowerMm, upperMm);
            };

            try {
                if (outputRoiNumber === null) {
                    if (!newName) {
                        enqueueSnackbar(
                            'A name is required to create a new structure',
                            { variant: 'error' }
                        );
                        return;
                    }
                    const ringTexture = await computeRingTexture();
                    await applyComputedGeometry(
                        newName,
                        { texture: ringTexture, label: 'Ring' },
                        makeApplyGeometryDeps({
                            environment,
                            inputRoi,
                            updateView,
                            getOrCreateDestination,
                        })
                    );
                    enqueueSnackbar(`Applied ring to "${newName}"`, {
                        variant: 'success',
                    });
                    onSuccess();
                } else {
                    const dest = await getOrCreateDestination(
                        outputRoiNumber,
                        newName,
                        inputRoi
                    );
                    if (!dest) {
                        return;
                    }
                    const ringTexture = await computeRingTexture();
                    dest.replaceTexture(ringTexture, 'Ring');
                    updateView();
                    recordContourEdit(
                        environment?.structureSet?.sopInstanceUID,
                        dest
                    );
                    enqueueSnackbar(`Applied ring to "${dest.name}"`, {
                        variant: 'success',
                    });
                    onSuccess();
                }
            } catch (e) {
                console.error('Ring apply error:', e);
                enqueueSnackbar(`Failed to apply ring: ${getErrorMessage(e)}`, {
                    variant: 'error',
                });
            } finally {
                setIsProcessing(false);
            }
        },
        [
            environment,
            inputRoi,
            isProcessing,
            getOrCreateDestination,
            updateView,
            onSuccess,
            consumeRingPreview,
        ]
    );

    return {
        isProcessing,
        handleApplyRing,
    };
}
